import { useState } from "react";

import { BrandLogo, brandColor } from "@/components/brands";
import { setupPrompt } from "@/content/clients";
import { partnerCode } from "@/lib/partner";
import { useCopy } from "@/lib/useCopy";
import { cn } from "@/lib/utils";

// Hero onboarding (DESIGN.md: Onboard pill, Tooltip), modelled on
// Cloudflare's "Onboard your agent" button. One press copies the one-line
// setup prompt; the agent fetches /agent-setup/prompt.txt and does the rest.

const AGENTS = ["claude", "codex", "opencode"] as const;

export function OnboardPill({ origin }: { origin: string }) {
  const { copied, copy } = useCopy();
  const [hinting, setHinting] = useState(false);
  const tip = copied ? "Setup prompt copied" : "Copies a setup prompt for your AI coding tool";

  return (
    <div className="relative inline-flex max-w-full">
      <span
        id="onboard-tip"
        role="status"
        className={cn(
          "ease pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-zsd-sm whitespace-nowrap",
          "line-border rounded-zsd-sm bg-zsd-surface-raised text-zsd-on-surface-raised px-zsd-md py-zsd-xs",
          "font-zsd-body-sm text-zsd-body-sm",
          copied || hinting ? "opacity-100" : "opacity-0",
        )}
      >
        {tip}
      </span>
      <button
        type="button"
        onClick={() => copy(setupPrompt(origin, partnerCode()))}
        onMouseEnter={() => setHinting(true)}
        onMouseLeave={() => setHinting(false)}
        onFocus={() => setHinting(true)}
        onBlur={() => setHinting(false)}
        aria-describedby="onboard-tip"
        className={cn(
          "brand ease inline-flex items-center gap-zsd-md max-w-full cursor-pointer",
          "control-border rounded-zsd-full bg-zsd-surface-raised text-zsd-on-surface-raised",
          "pl-zsd-xl pr-zsd-lg py-zsd-md hover:border-zsd-primary",
          "font-zsd-body-md text-zsd-body-md sm:font-zsd-body-lg sm:text-zsd-body-lg",
          copied && "outline-2 outline-offset-2 outline-zsd-primary outline",
        )}
      >
        <span className="whitespace-nowrap">
          Onboard your agent<span className="hidden sm:inline"> to Moonlauncher</span>
        </span>
        <span className="flex items-center gap-zsd-sm" aria-hidden="true">
          {AGENTS.map((id) => (
            <span key={id} style={{ "--brand": brandColor(id) } as React.CSSProperties}>
              <BrandLogo id={id} size={22} className="brand-logo" />
            </span>
          ))}
        </span>
      </button>
    </div>
  );
}
